import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import api from "services/api";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSigningOutRef = useRef(false);

  const signOutAndReset = useCallback(async (reasonLog) => {
    if (auth.currentUser && !isSigningOutRef.current) {
      isSigningOutRef.current = true;
      try {
        await signOut(auth);
      } catch (signOutError) {
        console.warn(reasonLog, signOutError);
      } finally {
        isSigningOutRef.current = false;
      }
    }

    setCurrentUser(null);
    setUserLoggedIn(false);
  }, []);

  const initializeUser = useCallback(async (user) => {
    if (!user) {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/members/me`);
      setCurrentUser({
        ...user,
        id: response.data.id,
        email: response.data.Email,
        Role: response.data.Role,
        Name: response.data.Name,
        Image: response.data.Image,
        ImageThumbnail: response.data.ImageThumbnail,
        HourlyRate: response.data.HourlyRate,
      });
      setUserLoggedIn(true);
    } catch (error) {
      console.error("Auth initialization failed:", error);
      const status = error.response?.status;
      const accountDisabled = error.response?.data?.accountDisabled;
      const userNotFound = error.response?.data?.error === "User not found in database";

      if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
        await signOutAndReset(
          "Could not fully sign out invalid account session:",
        );
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  }, [signOutAndReset]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initializeUser);
    return unsubscribe;
  }, [initializeUser]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const intervalId = setInterval(async () => {
      try {
        await api.get(`/members/me`);
      } catch (error) {
        const status = error.response?.status;
        const accountDisabled = error.response?.data?.accountDisabled;
        const userNotFound = error.response?.data?.error === "User not found in database";

        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid account session:",
          );
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [currentUser, signOutAndReset]);

  const value = {
    currentUser,
    userLoggedIn,
    loading,
    setCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
